import { useState, useEffect, useMemo } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Reveal } from "./Reveal";
import { api } from "@/lib/api";
import { useFooter } from "@/lib/useContent";
import { ShoppingBag, Loader2, Star, X, Package } from "lucide-react";

function ProductImage({ src, alt, className }) {
  if (!src) {
    return (
      <div className={`flex items-center justify-center bg-[#eef1f7] text-navy/30 ${className}`}>
        <Package className="h-10 w-10" />
      </div>
    );
  }
  return <img src={src} alt={alt} loading="lazy" className={className} />;
}

export const Tienda = () => {
  const [products, setProducts] = useState(null);
  const [category, setCategory] = useState("Todas");
  const [selected, setSelected] = useState(null);
  const [imgIdx, setImgIdx] = useState(0);
  const contact = useFooter();
  const wa = (contact.whatsapp || "").replace(/\D/g, "");

  useEffect(() => {
    api.get("/tienda/products").then(({ data }) => setProducts(data)).catch(() => setProducts([]));
  }, []);

  const categories = useMemo(
    () => ["Todas", ...Array.from(new Set((products || []).map((p) => p.category)))],
    [products]
  );
  const filtered = (products || []).filter((p) => category === "Todas" || p.category === category);

  const openProduct = (p) => { setImgIdx(0); setSelected(p); };
  const gallery = selected ? [selected.image_url, ...(selected.images || [])].filter(Boolean) : [];

  return (
    <section id="tienda" className="relative py-24 md:py-32 bg-[#eef1f7] grain-overlay">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="max-w-2xl">
          <span className="section-tag">Tienda oficial</span>
          <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-navy" style={{ fontFamily: "Outfit" }}>
            Vestí los colores del club
          </h2>
          <p className="mt-5 text-lg text-gray-600 leading-relaxed">
            Camisetas, indumentaria y accesorios oficiales. Los socios Oro y Plata acceden a descuentos exclusivos.
          </p>
        </Reveal>

        {products === null ? (
          <div className="mt-14 py-16 flex justify-center text-navy/40"><Loader2 className="h-7 w-7 animate-spin" /></div>
        ) : products.length === 0 ? (
          <div className="mt-14 py-16 text-center text-gray-400">
            <ShoppingBag className="h-10 w-10 mx-auto" />
            <p className="mt-3">Todavía no hay productos en la tienda.</p>
          </div>
        ) : (
          <>
            <div className="mt-10 flex flex-wrap gap-2">
              {categories.map((c) => (
                <button
                  key={c}
                  data-testid={`tienda-filter-${c}`}
                  onClick={() => setCategory(c)}
                  className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                    category === c ? "bg-navy text-white" : "bg-white text-navy border border-gray-200 hover:border-navy/40"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>

            <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-5 md:gap-7">
              {filtered.map((p, i) => {
                const agotado = p.status === "agotado";
                return (
                  <Reveal key={p.id} delay={(i % 4) * 0.06}>
                    <button
                      data-testid={`product-card-${p.slug}`}
                      onClick={() => openProduct(p)}
                      className="group w-full text-left bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                    >
                      <div className="relative aspect-square overflow-hidden bg-gray-100">
                        <ProductImage src={p.image_url} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        {p.highlight && (
                          <span className="absolute top-3 left-3 inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider bg-gold text-navy px-2.5 py-1 rounded-full">
                            <Star className="h-3 w-3" /> Destacado
                          </span>
                        )}
                        {agotado && (
                          <div className="absolute inset-0 bg-navy/55 flex items-center justify-center">
                            <span className="text-white font-bold text-sm uppercase tracking-wider border border-white/60 px-3 py-1.5 rounded-full">Agotado</span>
                          </div>
                        )}
                      </div>
                      <div className="p-4 sm:p-5">
                        <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">{p.category}</span>
                        <h3 className="mt-0.5 text-sm sm:text-base font-semibold text-navy leading-snug line-clamp-1">{p.name}</h3>
                        <div className="mt-2 text-lg font-bold text-navy" style={{ fontFamily: "Outfit" }}>{p.price || "Consultar"}</div>
                      </div>
                    </button>
                  </Reveal>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Detail modal */}
      <AnimatePresence>
        {selected && (
          <motion.div
            data-testid="product-modal"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setSelected(null)}
            className="fixed inset-0 z-[120] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl overflow-hidden max-w-3xl w-full max-h-[90vh] overflow-y-auto grid sm:grid-cols-2"
            >
              <div className="bg-[#eef1f7]">
                <ProductImage src={gallery[imgIdx]} alt={selected.name} className="w-full aspect-square object-cover" />
                {gallery.length > 1 && (
                  <div className="p-3 flex gap-2 justify-center">
                    {gallery.map((g, i) => (
                      <button key={i} onClick={() => setImgIdx(i)} className={`h-14 w-14 rounded-lg overflow-hidden border-2 ${imgIdx === i ? "border-navy" : "border-transparent"}`}>
                        <img src={g} alt="" className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div className="p-6 sm:p-7 relative">
                <button data-testid="product-modal-close" onClick={() => setSelected(null)} className="absolute top-4 right-4 h-9 w-9 rounded-lg bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-navy">
                  <X className="h-5 w-5" />
                </button>
                <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-400">{selected.category}</span>
                <h3 className="mt-1 text-2xl font-bold text-navy" style={{ fontFamily: "Outfit" }}>{selected.name}</h3>
                <div className="mt-3 flex items-center gap-3">
                  <span className="text-2xl font-extrabold text-navy" style={{ fontFamily: "Outfit" }}>{selected.price || "Consultar"}</span>
                  {selected.status === "agotado"
                    ? <span className="text-xs font-bold text-[#c0392b] bg-[#c0392b]/10 px-2.5 py-1 rounded-full">Agotado</span>
                    : <span className="text-xs font-bold text-green-700 bg-green-100 px-2.5 py-1 rounded-full">Disponible</span>}
                </div>
                {selected.description && <p className="mt-4 text-gray-600 leading-relaxed">{selected.description}</p>}
                <a
                  href={`https://wa.me/${wa}?text=${encodeURIComponent(`Hola! Me interesa el producto: ${selected.name}`)}`}
                  target="_blank" rel="noreferrer" data-testid="product-consult"
                  className="mt-6 inline-flex items-center gap-2 bg-navy text-white font-semibold px-5 py-3 rounded-xl hover:bg-[#16235a] transition-colors"
                >
                  <ShoppingBag className="h-5 w-5" /> Consultar / Comprar
                </a>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
